import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import AssignSurveyModal from './AssignSurveyModal';
import '../styles/PatientProfile.css';
import '../styles/AssignSurveyModal.css';
import '../styles/PatientGroups.css';

const PatientProfile = ({ patient, onBack, onUpdatePatient }) => {
  const { currentUser, getSurveyAssignmentsForPatient, patientGroups } = useAuth();
  const navigate = useNavigate();
  
  // Debug log to check patient data
  useEffect(() => {
    console.log('Patient data:', patient);
    console.log('Patient groups:', patient?.groups);
  }, [patient]);
  const [activeTab, setActiveTab] = useState('overview');
  const [isEditing, setIsEditing] = useState(false);
  const [showAssignSurvey, setShowAssignSurvey] = useState(false);
  const [refreshSurveys, setRefreshSurveys] = useState(0); // Add this line
  const [editData, setEditData] = useState({
    firstName: patient.firstName || patient.name?.split(' ')[0] || '',
    lastName: patient.lastName || patient.name?.split(' ')[1] || '',
    email: patient.email || '',
    phone: patient.phone || '',
    dateOfBirth: patient.dateOfBirth || '',
    address: patient.address || '',
    medicalHistory: patient.medicalHistory || '',
    emergencyContact: patient.emergencyContact || '',
    insurance: patient.insurance || '',
    allergies: patient.allergies || '',
    medications: patient.medications || '',
    groups: patient.groups || ['General'],
    conditions: patient.conditions || []
  });
  
  const [availableGroups, setAvailableGroups] = useState([]);
  const [newGroup, setNewGroup] = useState('');
  const [availableConditions, setAvailableConditions] = useState([
    'Hypertension', 'Diabetes', 'Asthma', 'COPD', 'Heart Disease', 
    'Arthritis', 'Anxiety', 'Depression', 'Obesity', 'High Cholesterol'
  ]);
  const [newCondition, setNewCondition] = useState('');

  const tabs = [
    { id: 'overview', label: 'Overview', icon: '👤' },
    { id: 'medical', label: 'Medical History', icon: '🏥' },
    { id: 'appointments', label: 'Appointments', icon: '📅' },
    { id: 'documents', label: 'Documents', icon: '📄' }
  ];

  const handleEditChange = (e) => {
    const { name, value, type, options } = e.target;
    
    if (type === 'select-multiple') {
      // Handle multi-select for groups
      const selectedOptions = Array.from(options)
        .filter(option => option.selected)
        .map(option => option.value);
      
      setEditData(prev => ({
        ...prev,
        [name]: selectedOptions
      }));
    } else {
      setEditData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };
  
  const handleAddGroup = (e) => {
    e.preventDefault();
    if (newGroup.trim() && !availableGroups.includes(newGroup.trim())) {
      setAvailableGroups(prev => [...prev, newGroup.trim()]);
      setEditData(prev => ({
        ...prev,
        groups: [...(prev.groups || []), newGroup.trim()]
      }));
      setNewGroup('');
    }
  };

  const handleAddCondition = (e) => {
    e.preventDefault();
    if (newCondition.trim() && !availableConditions.includes(newCondition.trim())) {
      setAvailableConditions(prev => [...prev, newCondition.trim()]);
    }
    if (newCondition.trim() && !editData.conditions?.includes(newCondition.trim())) {
      setEditData(prev => ({
        ...prev,
        conditions: [...(prev.conditions || []), newCondition.trim()]
      }));
      setNewCondition('');
    }
  };

  const handleRemoveCondition = (condition) => {
    setEditData(prev => ({
      ...prev,
      conditions: (prev.conditions || []).filter(c => c !== condition)
    }));
  };
  
  const handleRemoveGroup = (groupToRemove) => {
    setEditData(prev => ({
      ...prev,
      groups: prev.groups.filter(g => g !== groupToRemove)
    }));
  };
  
  // Load available groups when component mounts
  useEffect(() => {
    if (patientGroups && patientGroups.length > 0) {
      setAvailableGroups(patientGroups);
    } else {
      // Fallback to default groups if none provided
      setAvailableGroups([
        'General',
        'Diabetes',
        'Hypertension',
        'Post-Op',
        'High Risk',
        'Wellness'
      ]);
    }
  }, [patientGroups]);

  const handleSave = async (e) => {
    if (e) {
      e.preventDefault(); // Prevent form submission if called from a form
    }
    
    console.log('handleSave called with editData:', editData);
    
    try {
      const updatedPatient = {
        ...patient,
        ...editData,
        name: `${editData.firstName} ${editData.lastName}`.trim(),
        updatedAt: new Date().toISOString(),
        updatedBy: currentUser?.name || 'System',
        // Ensure groups array is always present and properly formatted
        groups: Array.isArray(editData.groups) 
          ? [...new Set(editData.groups.filter(Boolean))] // Remove duplicates and empty values
          : [],
        // Ensure conditions array is always present and properly formatted
        conditions: Array.isArray(editData.conditions)
          ? [...new Set(editData.conditions.filter(Boolean))] // Remove duplicates and empty values
          : []
      };
      
      console.log('Saving patient data:', {
        patientId: patient.id,
        updatedPatient,
        hasOnUpdatePatient: !!onUpdatePatient
      });
      
      if (onUpdatePatient) {
        console.log('Calling onUpdatePatient...');
        const result = await onUpdatePatient(updatedPatient);
        console.log('onUpdatePatient result:', result);
        
        if (result && result.success === false) {
          console.error('Failed to update patient:', result.error);
          alert(`Failed to save changes: ${result.error}`);
          return;
        }
        
        console.log('Patient updated successfully');
        setIsEditing(false);
      } else {
        console.error('onUpdatePatient function is not available');
        alert('Error: Cannot save changes - update function not available');
      }
    } catch (error) {
      console.error('Error in handleSave:', error);
      alert(`An error occurred while saving: ${error.message}`);
    }
  };

  const handleCancel = () => {
    setEditData({
      firstName: patient.firstName || patient.name?.split(' ')[0] || '',
      lastName: patient.lastName || patient.name?.split(' ')[1] || '',
      email: patient.email || '',
      phone: patient.phone || '',
      dateOfBirth: patient.dateOfBirth || '',
      address: patient.address || '',
      medicalHistory: patient.medicalHistory || '',
      emergencyContact: patient.emergencyContact || '',
      insurance: patient.insurance || '',
      allergies: patient.allergies || '',
      medications: patient.medications || ''
    });
    setIsEditing(false);
  };

  const getAge = () => {
    if (!patient.dateOfBirth) return 'N/A';
    const today = new Date();
    const birthDate = new Date(patient.dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const renderOverview = () => {
    // Handle both name formats: single name string or separate firstName/lastName
    const patientName = patient.name || `${patient.firstName || ''} ${patient.lastName || ''}`.trim();
    const initials = patientName 
      ? patientName.split(' ').map(n => n[0]).join('').toUpperCase()
      : '??';

    return (
      <div className="profile-overview">
        <div className="tab-content">
          <div className="profile-header">
            <div className="profile-header-content">
              <div className="patient-avatar">
                {initials}
              </div>
              <p className="patient-id">Patient ID: {patient.id || 'N/A'}</p>
              <div className="patient-stats">
                <span>Age: {getAge()}</span>
                <span>•</span>
                <span>Added: {patient.createdAt ? new Date(patient.createdAt).toLocaleDateString() : 'N/A'}</span>
                {patient.createdBy === 'self_registered' && (
                  <span className="text-xs text-gray-500 ml-2">
                    (Self-registered)
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="profile-actions">
            {!isEditing ? (
              <button onClick={() => setIsEditing(true)} className="edit-btn">
                Edit Patient
              </button>
            ) : (
              <div className="edit-actions">
                <button type="button" onClick={handleCancel} className="cancel-btn">Cancel</button>
                <button type="submit" className="save-btn">Save</button>
              </div>
            )}
          </div>
        </div>

        <div className="patient-details">
          <div className="details-grid">
            {/* Groups Card */}
            <div className="detail-card">
              <h3>Groups</h3>
              {!isEditing ? (
                <div className="detail-content">
                  {patient.groups && patient.groups.length > 0 ? (
                    <div className="patient-groups">
                      {patient.groups.map(group => (
                        <span key={group} className="group-tag view-only">
                          {group}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p>No groups assigned</p>
                  )}
                </div>
              ) : (
                <div className="edit-form">
                  <div className="form-group">
                    <div className="groups-container">
                      <div className="selected-groups">
                        {editData.groups && editData.groups.length > 0 ? (
                          editData.groups.map(group => (
                            <span key={group} className="group-tag">
                              {group}
                              <button 
                                type="button" 
                                className="remove-group"
                                onClick={() => handleRemoveGroup(group)}
                              >
                                &times;
                              </button>
                            </span>
                          ))
                        ) : (
                          <p>No groups selected</p>
                        )}
                      </div>
                      <div className="group-selector">
                        <select
                          multiple
                          name="groups"
                          value={editData.groups || []}
                          onChange={handleEditChange}
                          className="form-control"
                          size="3"
                        >
                          {availableGroups.map(group => (
                            <option key={group} value={group}>
                              {group}
                            </option>
                          ))}
                        </select>
                        <div className="add-group-form">
                          <div className="input-row">
                            <input
                              type="text"
                              value={newGroup}
                              onChange={(e) => setNewGroup(e.target.value)}
                              onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddGroup(e))}
                              placeholder="Add new group..."
                              className="form-control"
                            />
                            <button 
                              type="button" 
                              onClick={handleAddGroup}
                            >
                              Add
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Conditions Card */}
            <div className="detail-card">
              <h3>Conditions</h3>
              {!isEditing ? (
                <div className="detail-content">
                  {patient.conditions && patient.conditions.length > 0 ? (
                    <div className="patient-conditions">
                      {patient.conditions.map(condition => (
                        <span key={condition} className="condition-tag view-only">
                          {condition}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p>No conditions recorded</p>
                  )}
                </div>
              ) : (
                <div className="edit-form">
                  <div className="form-group">
                    <div className="conditions-container">
                      <div className="selected-conditions">
                        {editData.conditions && editData.conditions.length > 0 ? (
                          editData.conditions.map(condition => (
                            <span key={condition} className="condition-tag">
                              {condition}
                              <button 
                                type="button" 
                                className="remove-condition"
                                onClick={() => handleRemoveCondition(condition)}
                              >
                                &times;
                              </button>
                            </span>
                          ))
                        ) : (
                          <p>No conditions selected</p>
                        )}
                      </div>
                      <div className="condition-selector">
                        <select
                          multiple
                          name="conditions"
                          value={editData.conditions || []}
                          onChange={handleEditChange}
                          className="form-control"
                          size="3"
                        >
                          {availableConditions.map(condition => (
                            <option key={condition} value={condition}>
                              {condition}
                            </option>
                          ))}
                        </select>
                        <div className="add-condition-form">
                          <div className="input-row">
                            <input
                              type="text"
                              value={newCondition}
                              onChange={(e) => setNewCondition(e.target.value)}
                              onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddCondition(e))}
                              placeholder="Add new condition..."
                              className="form-control"
                            />
                            <button 
                              type="button" 
                              onClick={handleAddCondition}
                            >
                              Add
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            {/* Preferences Card - Only show if preferences exist */}
            {patient.preferences && (
              <div className="detail-card">
                <h3>Preferences</h3>
                <div className="detail-content">
                  {patient.preferences.preferredName && (
                    <div className="detail-item">
                      <label>Preferred Name:</label>
                      <span>{patient.preferences.preferredName}</span>
                    </div>
                  )}
                  {patient.preferences.techLevel && (
                    <div className="detail-item">
                      <label>Tech Comfort:</label>
                      <span>{patient.preferences.techLevel}</span>
                    </div>
                  )}
                  {patient.preferences.workSituation && (
                    <div className="detail-item">
                      <label>Work Situation:</label>
                      <span>{patient.preferences.workSituation}</span>
                    </div>
                  )}
                  {patient.preferences.motivations?.length > 0 && (
                    <div className="detail-item">
                      <label>Health Motivations:</label>
                      <span>{patient.preferences.motivations.join(', ')}</span>
                    </div>
                  )}
                  {patient.preferences.goals?.length > 0 && (
                    <div className="detail-item">
                      <label>Health Goals:</label>
                      <span>{patient.preferences.goals.join(', ')}</span>
                    </div>
                  )}
                  {patient.preferences.onboardingCompletedAt && (
                    <div className="detail-item">
                      <label>Onboarded On:</label>
                      <span>{new Date(patient.preferences.onboardingCompletedAt).toLocaleDateString()}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="detail-card">
              <h3>Contact Information</h3>
              {!isEditing ? (
                <div className="detail-content">
                  <div className="detail-item">
                    <label>Email:</label>
                    <span>{patient.email || 'Not provided'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Phone:</label>
                    <span>{patient.phone || 'Not provided'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Address:</label>
                    <span>{patient.address || 'Not provided'}</span>
                  </div>
                  {patient.groups && patient.groups.length > 0 && (
                    <div className="detail-item">
                      <label>Groups:</label>
                      <div className="patient-groups">
                        {patient.groups.map(group => (
                          <span key={group} className="group-tag view-only">
                            {group}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="edit-form">
                  <div className="form-row">
                    <div className="form-group">
                      <label>First Name</label>
                      <input
                        type="text"
                        name="firstName"
                        value={editData.firstName}
                        onChange={handleEditChange}
                      />
                    </div>
                    <div className="form-group">
                      <label>Last Name</label>
                      <input
                        type="text"
                        name="lastName"
                        value={editData.lastName}
                        onChange={handleEditChange}
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Email</label>
                    <input
                      type="email"
                      name="email"
                      value={editData.email}
                      onChange={handleEditChange}
                      className="form-control"
                      disabled={!isEditing}
                    />
                  </div>
                  <div className="form-group">
                    <label>Phone</label>
                    <input
                      type="tel"
                      name="phone"
                      value={editData.phone}
                      onChange={handleEditChange}
                    />
                  </div>
                  <div className="form-group">
                    <label>Address</label>
                    <textarea
                      name="address"
                      value={editData.address}
                      onChange={handleEditChange}
                      rows="2"
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="detail-card">
              <h3>Personal Information</h3>
              <div className="detail-content">
                <div className="detail-item">
                  <label>Date of Birth:</label>
                  {!isEditing ? (
                    <span>{patient.dateOfBirth ? new Date(patient.dateOfBirth).toLocaleDateString() : 'Not provided'}</span>
                  ) : (
                    <input
                      type="date"
                      name="dateOfBirth"
                      value={editData.dateOfBirth}
                      onChange={handleEditChange}
                    />
                  )}
                </div>
                <div className="detail-item">
                  <label>Emergency Contact:</label>
                  {!isEditing ? (
                    <span>{patient.emergencyContact || 'Not provided'}</span>
                  ) : (
                    <input
                      type="text"
                      name="emergencyContact"
                      value={editData.emergencyContact}
                      onChange={handleEditChange}
                      placeholder="Name and phone number"
                    />
                  )}
                </div>
                <div className="detail-item">
                  <label>Insurance:</label>
                  {!isEditing ? (
                    <span>{patient.insurance || 'Not provided'}</span>
                  ) : (
                    <input
                      type="text"
                      name="insurance"
                      value={editData.insurance}
                      onChange={handleEditChange}
                      placeholder="Insurance provider and policy"
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderMedical = () => (
    <div className="tab-content">
      <div className="medical-section">
        <div className="medical-grid">
          <div className="medical-card">
            <h3>Medical History</h3>
            {!isEditing ? (
              <div className="medical-content">
                {patient.medicalHistory || 'No medical history recorded'}
              </div>
            ) : (
              <textarea
                name="medicalHistory"
                value={editData.medicalHistory}
                onChange={handleEditChange}
                rows="4"
                placeholder="Enter medical history..."
              />
            )}
          </div>

          <div className="medical-card">
            <h3>Allergies</h3>
            {!isEditing ? (
              <div className="medical-content">
                {patient.allergies || 'No known allergies'}
              </div>
            ) : (
              <textarea
                name="allergies"
                value={editData.allergies}
                onChange={handleEditChange}
                rows="3"
                placeholder="List any allergies..."
              />
            )}
          </div>

          <div className="medical-card">
            <h3>Current Medications</h3>
            {!isEditing ? (
              <div className="medical-content">
                {patient.medications || 'No current medications'}
              </div>
            ) : (
              <textarea
                name="medications"
                value={editData.medications}
                onChange={handleEditChange}
                rows="3"
                placeholder="List current medications..."
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const [surveyData, setSurveyData] = useState({
    loading: true,
    surveys: [],
    error: null
  });

  useEffect(() => {
    const fetchSurveys = async () => {
      try {
        setSurveyData(prev => ({ ...prev, loading: true }));
        const surveys = await getSurveyAssignmentsForPatient(patient.id);
        setSurveyData({
          loading: false,
          surveys: Array.isArray(surveys) ? surveys : [],
          error: null
        });
      } catch (error) {
        console.error('Error loading surveys:', error);
        setSurveyData({
          loading: false,
          surveys: [],
          error: 'Failed to load surveys. Please try again.'
        });
      }
    };

    if (getSurveyAssignmentsForPatient && patient?.id) {
      fetchSurveys();
    } else {
      setSurveyData(prev => ({ ...prev, loading: false }));
    }
  }, [getSurveyAssignmentsForPatient, patient?.id, refreshSurveys]);

  const renderSurveys = () => {
    const { loading, surveys: patientSurveys, error } = surveyData;
    
    if (loading) {
      return (
        <div className="tab-content">
          <div className="loading-state">Loading surveys...</div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="tab-content">
          <div className="error-state">{error}</div>
        </div>
      );
    }
    
    return (
      <div className="tab-content">
        <div className="surveys-section">
          <div className="section-header">
            <h3>Patient Surveys</h3>
            <button 
              className="primary-btn"
              onClick={() => setShowAssignSurvey(true)}
              disabled={!patient?.id}
            >
              + Assign Survey
            </button>
            {showAssignSurvey && (
              <AssignSurveyModal
                onClose={() => setShowAssignSurvey(false)}
                selectedPatient={patient}
                onSurveyAssigned={() => {
                  setRefreshSurveys(prev => prev + 1); // Trigger a refresh of the survey list
                  setShowAssignSurvey(false);
                }}
              />
            )}
          </div>
          
          <div className="surveys-stats">
            <div className="stat-card">
              <div className="stat-number">{patientSurveys.length}</div>
              <div className="stat-label">Total Assigned</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">{patientSurveys.filter(s => s.status === 'completed').length}</div>
              <div className="stat-label">Completed</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">{patientSurveys.filter(s => s.status === 'assigned').length}</div>
              <div className="stat-label">Pending</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">{patientSurveys.filter(s => s.status === 'overdue').length}</div>
              <div className="stat-label">Overdue</div>
            </div>
          </div>
          
          {patientSurveys.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">📋</div>
              <h4>No Surveys Assigned</h4>
              <p>This patient hasn't been assigned any surveys yet. Click "Assign Survey" to get started.</p>
            </div>
          ) : (
            <div className="surveys-list">
              {patientSurveys.map((survey) => (
                <div key={survey.id} className="survey-item">
                  <div className="survey-header">
                    <div className="survey-title">{survey.surveyName}</div>
                    <div className="survey-badges">
                      <span className={`priority-badge ${survey.priority}`}>
                        {survey.priority.charAt(0).toUpperCase() + survey.priority.slice(1)}
                      </span>
                      <span className={`status-badge ${survey.status}`}>
                        {survey.status.charAt(0).toUpperCase() + survey.status.slice(1)}
                      </span>
                    </div>
                  </div>
                  <div className="survey-details">
                    <div className="survey-meta">
                      <span>📋 {survey.estimatedTime}</span>
                      <span>📅 Due: {new Date(survey.dueDate).toLocaleDateString()}</span>
                      <span>👤 Assigned by: {survey.assignedBy}</span>
                    </div>
                    {survey.notes && (
                      <div className="survey-notes">
                        <strong>Notes:</strong> {survey.notes}
                      </div>
                    )}
                    <div className="survey-actions">
                      <button className="secondary-btn">View Details</button>
                      {survey.status === 'completed' ? (
                        <button 
                          className="secondary-btn"
                          onClick={() => navigate(`/survey-response/${survey.id}`)}
                        >
                          View Responses
                        </button>
                      ) : (
                        <button className="secondary-btn">Send Reminder</button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderAppointments = () => (
    <div className="tab-content">
      <div className="appointments-section">
        <div className="section-header">
          <h3>Appointments</h3>
          <button className="primary-btn">+ Schedule Appointment</button>
        </div>
        <div className="empty-state">
          <div className="empty-icon">📅</div>
          <h4>No Appointments Scheduled</h4>
          <p>No appointments have been scheduled for this patient yet.</p>
        </div>
      </div>
    </div>
  );

  const renderDocuments = () => (
    <div className="tab-content">
      <div className="documents-section">
        <div className="section-header">
          <h3>Patient Documents</h3>
          <button className="primary-btn">+ Upload Document</button>
        </div>
        <div className="empty-state">
          <div className="empty-icon">📄</div>
          <h4>No Documents Uploaded</h4>
          <p>No documents have been uploaded for this patient yet.</p>
        </div>
      </div>
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return renderOverview();
      case 'medical':
        return renderMedical();
      case 'surveys':
        return renderSurveys();
      case 'appointments':
        return renderAppointments();
      case 'documents':
        return renderDocuments();
      default:
        return renderOverview();
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    console.log('Form submitted, calling handleSave...');
    await handleSave(e);
  };

  return (
    <div className="patient-profile">
      <div className="profile-nav">
        <button type="button" onClick={onBack} className="back-button">
          ← Back to Patients
        </button>
      </div>
      <div className="profile-header">
        <div className="patient-info">
          <div className="patient-name-container">
            <div className="patient-avatar">
              {patient.name ? patient.name.charAt(0).toUpperCase() : (patient.firstName ? patient.firstName.charAt(0).toUpperCase() : '')}
            </div>
            <h1>{patient.name || `${patient.firstName} ${patient.lastName}`}</h1>
          </div>
          <div className="patient-meta">
            <span>📧 {patient.email}</span>
            <span>📞 {patient.phone}</span>
            <span>🎂 {patient.dateOfBirth || 'Not provided'}</span>
            {patient.id && <span>🪪 {patient.id}</span>}
          </div>
        </div>
        <div className="patient-actions">
          <button className="primary" onClick={() => alert('Book appointment functionality coming soon')}>
            <span>📅</span> Book an appointment
          </button>
          <button onClick={() => alert('Message patient functionality coming soon')}>
            <span>💬</span> Message patient
          </button>
        </div>
      </div>

      <div className="profile-tabs">
        <div className="tab-nav">
          <button 
            className={`tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            Overview
          </button>
          <button 
            className={`tab-btn ${activeTab === 'medical' ? 'active' : ''}`}
            onClick={() => setActiveTab('medical')}
          >
            Medical
          </button>
          <button 
            className={`tab-btn ${activeTab === 'surveys' ? 'active' : ''}`}
            onClick={() => setActiveTab('surveys')}
          >
            Surveys
          </button>
          <button 
            className={`tab-btn ${activeTab === 'appointments' ? 'active' : ''}`}
            onClick={() => setActiveTab('appointments')}
          >
            Appointments
          </button>
          <button 
            className={`tab-btn ${activeTab === 'documents' ? 'active' : ''}`}
            onClick={() => setActiveTab('documents')}
          >
            Documents
          </button>
        </div>

        <form onSubmit={handleFormSubmit} className="profile-content">
          {renderTabContent()}
        </form>
      </div>

      {/* Modal rendering is handled within the renderSurveys function */}
    </div>
  );
};

export default PatientProfile;
